import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@mnz-core/injectors/app-configuration';
import { MNZ_PLATFORM } from '@mnz-core/injectors/platform';
import { Platform } from '@mnz-core/models/_enums';
import { IAppConfiguration } from '@mnz-core/models/app-configuration';
import * as Sentry from '@sentry/browser';

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  constructor(
    @Inject(MNZ_PLATFORM) private readonly _platform: Platform,
    @Inject(APP_CONFIG) private readonly _config: IAppConfiguration
  ) {}

  private readonly _debugPlatforms = [
    Platform.ACCEPTANCE,
    Platform.TEST,
    Platform.DEVELOPMENT
  ];

  /**
   * Log a message to sentry
   * @param errorOrMessage the error/exception or just a simple message
   * @param level error, critical, warning, info, etc
   * @param context provide extra information
   */
  public log(errorOrMessage: any, level: Sentry.Severity, context?: Object): void {
    Sentry.withScope(scope => {
      if (context) {
        scope.setExtra('context', context);
      }

      scope.setExtra('errorOrMessage', { error: errorOrMessage.message || errorOrMessage });
      scope.setTag('language', this._config.relay42_config.language);

      Sentry.captureMessage(errorOrMessage, level);

      if (this._debugPlatforms.includes(this._platform)) {
        console.error(`[sentry-l] => `, { errorOrMessage, context });
      }
    });
  }

  /**
   * Log an error to sentry
   * @param error the error/exception
   * @param context provide extra information
   */
  public logError(error: any, context?: Object): void {
    Sentry.withScope(scope => {
      if (context) {
        scope.setExtra('context', context);
      }

      scope.setExtra('errorMessage', { error: error.message || error });
      scope.setTag('language', this._config.relay42_config.language);

      Sentry.captureException(error.originalError || error);

      if (this._debugPlatforms.includes(this._platform)) {
        console.error(`[sentry-e] => `, { error, context });
      }
    });
  }

  /**
   * Log an info message to sentry
   * @param message the message
   * @param context provide extra information
   */
  public logInfo(message: string, context?: Object): void {
    Sentry.withScope(scope => {
      if (context) {
        scope.setExtra('context', context);
      }

      scope.setTag('language', this._config.relay42_config.language);
      Sentry.captureMessage(message, Sentry.Severity.Info);

      if (this._debugPlatforms.includes(this._platform)) {
        console.info(`[sentry-i] => `, { message, context });
      }
    });
  }

  /**
   * Log a debug message to sentry
   * @param message the message
   * @param context provide extra information
   */
  public logDebug(message: string, context?: Object): void {
    Sentry.withScope(scope => {
      if (context) {
        scope.setExtra('context', context);
      }

      scope.setTag('language', this._config.relay42_config.language);
      Sentry.captureMessage(message, Sentry.Severity.Debug);

      if (this._debugPlatforms.includes(this._platform)) {
        console.info(`[sentry-d] => `, { message, context });
      }
    });
  }

  /**
   * Log a warning message to sentry
   * @param message the message
   * @param context provide extra information
   */
  public logWarning(message: string, context?: Object): void {
    Sentry.withScope(scope => {
      if (context) {
        scope.setExtra('context', context);
      }

      scope.setTag('language', this._config.relay42_config.language);
      Sentry.captureMessage(message, Sentry.Severity.Warning);

      if (this._debugPlatforms.includes(this._platform)) {
        console.info(`[sentry-w] => `, { message, context });
      }
    });
  }
}
